/* @flow */

import { partition, map } from 'lodash';

import { API_CALL, SET_CURRENT_GROUP } from '../constants/actionTypes';
import {
  CREATE_GROUP,
  DELETE_GROUP,
  GROUP,
  GROUPS,
  UPDATE_GROUP,
  ADD_FRIEND_TO_GROUP,
  REMOVE_FRIEND_FROM_GROUP
} from '../constants/apiTypes';
import { NEW_GROUP_ID } from '../constants/friendConstants';
import friendsSelector from '../selectors/friends/friendsSelector';
import * as groupsApi from '../api/groups';
import * as friendsApi from '../api/friends';
import * as friendRequestActions from './friendRequestActions';

export function fetchGroup(groupId: number) {
  return {
    type: API_CALL,
    api: GROUP,
    request: groupsApi.getGroup(groupId, { embed: ['members'] })
  };
}

export function setCurrentGroup(groupId: number) {
  return {
    type: SET_CURRENT_GROUP,
    groupId
  };
}

export function fetchGroups() {
  return {
    type: API_CALL,
    api: GROUPS,
    request: groupsApi.getGroups()
  };
}

export function fetchGroupsWithMembers() {
  return {
    type: API_CALL,
    api: GROUPS,
    request: groupsApi.getGroups({ embed: ['members'] })
  };
}

export function deleteGroup(groupId: number) {
  let request = Promise.resolve();
  if (groupId !== NEW_GROUP_ID) {
    request = groupsApi.deleteGroup(groupId);
  }
  return {
    type: API_CALL,
    api: DELETE_GROUP,
    request,
    id: groupId
  };
}

/**
 * Add a friend to a single group
 * @param {number | string} groupId The group to add to
 * @param {number} friendId The friend who will be added
 */
export function addToGroup(groupId: number | string, friendId: number) {
  let request = Promise.resolve();
  if (groupId !== NEW_GROUP_ID) {
    request = friendsApi.addFriendToGroups(friendId, [groupId]);
  }
  return {
    type: API_CALL,
    api: ADD_FRIEND_TO_GROUP,
    request,
    friendId,
    groupId
  };
}

/**
 * Remove a friend from a single group
 * @param {number | string} groupId The group to remove from
 * @param {number} friendId The friend who will be removed
 */
export function removeFromGroup(groupId: number | string, friendId: number) {
  let request = Promise.resolve();
  if (groupId !== NEW_GROUP_ID) {
    request = friendsApi.removeFriendFromGroups(friendId, [groupId]);
  }
  return {
    type: API_CALL,
    api: REMOVE_FRIEND_FROM_GROUP,
    request,
    friendId,
    groupId
  };
}

export function createGroup(group: FriendGroup) {
  return (dispatch: Dispatch<*>, getState: () => GlobalState) => {
    const state = getState();
    const [friends, requests] = partition(group.members, id => !!state.friends.friends[id]);

    const data = {
      name: group.name,
      friends
    };

    dispatch({
      type: API_CALL,
      api: CREATE_GROUP,
      groupId: group.id,
      request: groupsApi.createGroup(data).then(res => {
        if (requests.length > 0) {
          const allFriendIds = new Set(map(friendsSelector(state), 'id'));
          const newRequests = requests.filter(id => !allFriendIds.has(id));
          // the group create needs to be successful before we can send the requests
          dispatch(friendRequestActions.createFriendRequests(newRequests, res.data.id));
        }
        return res;
      })
    });
  };
}

export function updateGroup(groupId: number) {
  return (dispatch: Dispatch<*>, getState: () => GlobalState) => {
    const state = getState();
    const editGroup = state.friendGroups.groups[groupId];
    if (!editGroup) {
      throw new Error('Found no group information in reducer to update.');
    }

    const [friends, requests] = partition(editGroup.members, id => !!state.friends.friends[id]);
    const allFriendIds = new Set(map(friendsSelector(state, { friendRequests: true }), 'id'));
    const newRequests = requests.filter(id => !allFriendIds.has(id));

    // back end doesn't want friend request ID's
    // In theory, because you can't add or remove existing friend requests to a group
    // this means we shoul never be sending it through
    const data = {
      name: editGroup.name,
      friends
    };

    dispatch({
      type: API_CALL,
      api: UPDATE_GROUP,
      request: groupsApi.updateGroup(editGroup.id, data).then(res => {
        // make sure we only ask for friend requests for users we don't
        // already have on request or as users already
        if (newRequests.length > 0) {
          dispatch(friendRequestActions.createFriendRequests(newRequests, editGroup.id));
        }
        return res;
      }),
      groupId: editGroup.id
    });
  };
}

export function saveGroupName(groupId: number, name: string) {
  return (dispatch: Dispatch<*>, getState: () => GlobalState) => {
    const state = getState();
    const friendGroups = state.friendGroups;
    const group = friendGroups.groups[groupId];
    if (!group) {
      throw new Error('Found no group information in reducer to save.');
    }

    const data = {
      name,
      // back end only wants to know about the friends in the list
      // don't include pending requests that are in the group...
      friends: group.members.filter(id => !!state.friends.friends[id])
    };

    const request = () => {
      if (groupId === NEW_GROUP_ID) {
        return groupsApi.createGroup(data);
      }
      return groupsApi.updateGroup(groupId, data);
    };
    const api = groupId === NEW_GROUP_ID ? CREATE_GROUP : UPDATE_GROUP;

    dispatch({
      type: API_CALL,
      api,
      request: request(),
      groupId
    });
  };
}
